import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import AWLogo from "@/components/AWLogo";
import { waLink, WhatsAppIcon } from "@/lib/whatsapp";

const LINKS = [
  { to: "/models", label: "Models" },
  { to: "/offers", label: "Offers" },
  { to: "/bulk", label: "Bulk Purchase" },
  { to: "/about", label: "About" },
];

const BRAND_LINKS = [
  { to: "/brands/honda", label: "Honda" },
  { to: "/brands/proton", label: "Proton" },
  { to: "/brands/jaecoo", label: "Jaecoo & Omoda" },
];

export default function Header() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 border-b border-black/10 bg-white/85 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 md:h-20 md:px-10">
          <Link to="/" data-testid="header-home-link" className="flex items-center gap-3" onClick={() => setOpen(false)}>
            <AWLogo size={38} />
            <div className="leading-none">
              <div className="font-display text-lg font-800 font-extrabold tracking-tight">AUTOWORLD</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-500">Honda · Proton · Jaecoo &amp; Omoda</div>
            </div>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="desktop-nav">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="nav-link font-mono text-[11px] uppercase tracking-[0.22em] text-neutral-700 hover:text-black"
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="header-whatsapp-button"
              className="flex items-center gap-2 bg-black px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-neutral-800"
            >
              <WhatsAppIcon className="h-4 w-4" /> WhatsApp
            </a>
          </nav>

          <button
            data-testid="mobile-menu-button"
            className="flex h-11 w-11 items-center justify-center border border-black lg:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] flex flex-col bg-black text-white"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex h-16 items-center justify-between px-5">
              <div className="flex items-center gap-3">
                <AWLogo size={38} invert />
                <span className="font-display text-lg font-extrabold tracking-tight">AUTOWORLD</span>
              </div>
              <button
                data-testid="mobile-menu-close"
                className="flex h-11 w-11 items-center justify-center border border-white/30"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8">
              {[...LINKS, ...BRAND_LINKS].map((l, i) => (
                <motion.div
                  key={l.to + l.label}
                  initial={{ opacity: 0, x: -32 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                    className="block border-b border-white/10 py-3 font-display text-3xl font-bold uppercase tracking-tight hover:pl-3 transition-all"
                    onClick={() => setOpen(false)}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <a
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-menu-whatsapp"
                className="flex items-center justify-center gap-3 bg-white py-4 font-mono text-xs uppercase tracking-[0.25em] text-black"
              >
                <WhatsAppIcon className="h-5 w-5" /> Chat on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
